import { useRef, useEffect, useState } from 'react';

const COOKING_POT_URL = "https://customer-assets.emergentagent.com/job_precision-cookware/artifacts/p4ryrjms_Cooking%20pot.png";

export const BrandStatement = () => {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      
      const rect = sectionRef.current.getBoundingClientRect();
      const sectionHeight = sectionRef.current.offsetHeight;
      const viewportHeight = window.innerHeight;
      
      // Progress based on how much of section is in view
      const visibleTop = Math.max(0, viewportHeight - rect.top);
      const visibleBottom = Math.max(0, rect.bottom);
      const visibleHeight = Math.min(visibleTop, visibleBottom, sectionHeight);
      const progress = Math.min(1, Math.max(0, visibleTop / (sectionHeight)));
      
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const specs = [
    {
      title: "SS316 Interior",
      description: "Hygienic, non-reactive, and a pleasure to cook in.",
    },
    {
      title: "Aluminium Core",
      description: "Heat where you need it. Even, fast, precise.",
    },
    {
      title: "SS403 Exterior",
      description: "Ready for every hob. Constructed to perform.",
    },
  ];

  // Quote words for scroll reveal
  const quoteWords = [
    "The", "kitchen", "is", "where", "intention",
    "becomes", "something", "tangible.",
    "We", "make", "what", "you", "reach", "for."
  ];

  return (
    <section
      ref={sectionRef}
      id="craft"
      data-testid="brand-statement-section"
      className="relative min-h-[200vh] bg-tex-bg"
    >
      {/* TECHNIQUE 1: Pinned Background */}
      <div className="sticky top-0 h-screen overflow-hidden flex items-center justify-center">
        {/* Background Image - fades in */}
        <div 
          className="absolute inset-0 z-0 flex items-center justify-center"
          style={{
            opacity: Math.min(1, scrollProgress * 2),
            transform: `scale(${0.9 + scrollProgress * 0.1})`
          }}
        >
          <img
            src={COOKING_POT_URL}
            alt="Tex-Style Company Cooking Pot"
            className="max-w-md w-full h-auto object-contain opacity-20"
          />
        </div>

        {/* Violet Gradient */}
        <div 
          className="absolute inset-0 z-5"
          style={{
            background: `radial-gradient(ellipse at center, rgba(45, 26, 92, ${0.2 + scrollProgress * 0.3}) 0%, transparent 60%)`
          }}
        />

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center">
          {/* TECHNIQUE 2: Scroll-driven quote reveal */}
          <blockquote
            data-testid="brand-quote"
            className="mb-16"
          >
            <p className="text-editorial-section text-white text-center">
              "
              {quoteWords.map((word, i) => {
                const wordProgress = Math.min(1, Math.max(0, (scrollProgress - i * 0.03) * 5));
                const isHighlight = word === "tangible.";
                return (
                  <span
                    key={i}
                    style={{
                      opacity: wordProgress,
                      transform: `translateY(${20 - wordProgress * 20}px)`,
                      display: 'inline-block',
                      marginRight: '0.3em',
                      transition: 'none'
                    }}
                    className={isHighlight ? 'text-tex-violet-bright' : ''}
                  >
                    {word}
                  </span>
                );
              })}
              "
            </p>
          </blockquote>

          {/* Thin Divider - appears after quote */}
          <div 
            className="w-24 h-[1px] bg-tex-violet/50 mx-auto mb-16"
            style={{
              opacity: Math.min(1, Math.max(0, (scrollProgress - 0.4) * 3)),
              transform: `scaleX(${Math.min(1, Math.max(0, (scrollProgress - 0.4) * 3))})`
            }}
          />

          {/* 3-Column Specs - appear after divider */}
          <div
            data-testid="specs-grid"
            className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-0"
            style={{
              opacity: Math.min(1, Math.max(0, (scrollProgress - 0.5) * 3))
            }}
          >
            {specs.map((spec, index) => (
              <div
                key={spec.title}
                data-testid={`spec-${index + 1}`}
                className={`text-center px-6 ${
                  index < specs.length - 1 ? 'md:border-r md:border-white/10' : ''
                }`}
                style={{
                  opacity: Math.min(1, Math.max(0, (scrollProgress - 0.5 - index * 0.05) * 4)),
                  transform: `translateY(${Math.max(0, 20 - (scrollProgress - 0.5 - index * 0.05) * 80)}px)`
                }}
              >
                <h3 className="font-mono text-[11px] font-normal uppercase tracking-[0.2em] text-tex-warm-white mb-3">
                  {spec.title}
                </h3>
                <p className="font-raleway text-sm font-light text-tex-muted leading-relaxed">
                  {spec.description}
                </p>
              </div>
            ))}
          </div>

          {/* Warranty Badge - appears last */}
          <div
            data-testid="warranty-badge"
            className="flex justify-center mt-20"
            style={{
              opacity: Math.min(1, Math.max(0, (scrollProgress - 0.7) * 4)),
              transform: `scale(${Math.min(1, 0.8 + (scrollProgress - 0.7) * 0.8)})`
            }}
          >
            <div className="relative flex flex-col items-center justify-center w-32 h-32 rounded-full border border-tex-gold/60">
              <span className="font-raleway text-4xl font-extralight text-tex-gold">10</span>
              <span className="font-raleway text-[9px] font-semibold uppercase tracking-[0.15em] text-tex-gold">
                Year Warranty
              </span>
              <span className="absolute -bottom-8 font-raleway text-[10px] text-tex-gold/70 whitespace-nowrap">
                Our commitment to craftsmanship.
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandStatement;
